import { useEffect, useId, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ROUTES } from '@/config/routes';
import { AppIcon } from '@/shared/components/ui/AppIcon/AppIcon';
import type { SidebarProjectSlotProps } from '@/shared/components/layout/Sidebar';
import { useAllModelsQuery } from '../hooks/useProjects';

function ProjectLinks({
  filter,
  onNavigate,
}: {
  readonly filter: string;
  readonly onNavigate: () => void;
}): React.JSX.Element {
  const query = useAllModelsQuery();
  const projects = useMemo(() => {
    const term = filter.trim().toLowerCase();
    return (query.data ?? [])
      .filter((project) => `${project.title} ${project.name}`.toLowerCase().includes(term))
      .map((project) => ({
        id: project.id,
        label: project.title.trim() || project.name.trim() || `Project ${String(project.id)}`,
      }))
      .sort((left, right) => left.label.localeCompare(right.label) || Number(left.id) - Number(right.id));
  }, [filter, query.data]);

  if (query.isPending) return <p className="project-tree__status" role="status">Loading projects…</p>;
  if (query.isError) {
    return (
      <div className="project-tree__status" role="alert">
        <p>Projects could not be loaded.</p>
        <button type="button" className="ui-button" onClick={() => { void query.refetch(); }}>Retry projects</button>
      </div>
    );
  }
  if (projects.length === 0) {
    return <p className="project-tree__status" role="status">{filter.trim() ? 'No matching projects.' : 'No projects available.'}</p>;
  }
  return (
    <ul className="project-tree__list" aria-label="Projects">
      {projects.map((project) => (
        <li key={project.id}>
          <NavLink
            to={ROUTES.projectDetail(Number(project.id))}
            onClick={onNavigate}
            title={project.label}
            className={({ isActive }) => `project-tree__link${isActive ? ' is-active' : ''}`}
          >
            <span aria-hidden="true" className="project-tree__branch" />
            <span>{project.label}</span>
          </NavLink>
        </li>
      ))}
    </ul>
  );
}

/** Disclosure navigation keeps native links and Tab/Enter semantics inside the sidebar. */
export function ProjectSidebarTree({
  label,
  compact,
  onNavigate,
  onExpandSidebar,
}: SidebarProjectSlotProps): React.JSX.Element {
  const { pathname } = useLocation();
  // Collapsed by default so the rest of the navigation is visible without scrolling; the
  // effect below opens it whenever a project is the current page.
  const [expanded, setExpanded] = useState(() => pathname.startsWith(`${ROUTES.projects}/`));
  const [filter, setFilter] = useState('');
  const childrenId = useId();
  const filterId = useId();
  const open = !compact && expanded;

  // Reveal the active project's branch on navigation/back/forward, while still allowing
  // the user to close it explicitly without leaving the current project.
  useEffect(() => {
    if (pathname.startsWith(`${ROUTES.projects}/`)) {
      setExpanded(true);
      setFilter('');
    }
  }, [pathname]);

  return (
    <div className="project-tree">
      <div className={`project-tree__heading${compact ? ' project-tree__heading--compact' : ''}`}>
        {compact ? null : (
          <NavLink
            key="projects"
            to={ROUTES.projects}
            onClick={onNavigate}
            className={({ isActive }) => `app-sidebar__item${isActive ? ' is-active' : ''}`}
          >
            <span className="app-sidebar__icon">
              <AppIcon name="projects" size={20} />
            </span>
            <span className="app-sidebar__label">{label}</span>
          </NavLink>
        )}
        <button
          key="toggle"
          type="button"
          className="project-tree__toggle"
          aria-label={open ? 'Collapse project tree' : 'Expand project tree'}
          aria-expanded={open}
          aria-controls={childrenId}
          title={compact ? 'Expand project tree' : undefined}
          onClick={() => {
            if (compact) {
              setExpanded(true);
              onExpandSidebar();
            } else {
              setExpanded((current) => !current);
            }
          }}
        >
          <AppIcon name={compact ? 'projects' : open ? 'chevronUp' : 'chevronDown'} size={compact ? 20 : 16} />
        </button>
      </div>
      <div id={childrenId} hidden={!open} className="project-tree__children">
        {open ? (
          <>
            <label className="project-tree__filter-label" htmlFor={filterId}>Find project</label>
            <input id={filterId} type="search" value={filter} placeholder="Project name"
              onChange={(event) => setFilter(event.currentTarget.value)} className="project-tree__filter" />
            <ProjectLinks filter={filter} onNavigate={onNavigate} />
          </>
        ) : null}
      </div>
    </div>
  );
}
